import { components, paths } from "@/api-schema";
import {
  PathsV1GroupSearchPostParametersQueryOrderByDirection,
  PathsV1GroupSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useGroupDeleteMutation } from "@/common/api/group/use-group-delete.mutation";
import { useGroupRestoreMutation } from "@/common/api/group/use-group-restore.mutation";
import { useGroupSearchQuery } from "@/common/api/group/use-group-search.query";
import { DeletedStatus, DeletedStatusFilter } from "@/components/filter/predefined/deleted-status-filter";
import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RowWithIdentifier } from "../_helpers/row-identifier";
import { TableFeatures } from "../types";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { groupTableDefaultColumns } from "./group-table-columns";

type Group = components["schemas"]["Group"] & RowWithIdentifier;
type GroupSorting = NonNullable<paths["/v1/group/search"]["post"]["parameters"]["query"]["orderBy"]>;
type GroupQueryParams = paths["/v1/group/search"]["post"]["parameters"]["query"];
type GroupRequestBody = paths["/v1/group/search"]["post"]["requestBody"]["content"]["application/json"];

const DEFAULT_COLUMNS = [...Object.values(groupTableDefaultColumns)];

const GROUP_TABLE_CONFIG: PredefinedTableConfig<GroupSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1GroupSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1GroupSearchPostParametersQueryOrderByField.Name,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("ram:SearchForGroup"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: true,
    showView: true,
    showDelete: true,
  } as const,
  sortingMapping: {
    name: ["name"],
    deletedAt: ["deletedAt"],
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
  } as const,
} as const;

const GROUP_TABLE_QUERY_HOOKS: DataFetchHooks<Group, GroupQueryParams, GroupRequestBody> = {
  useSearchQuery: useGroupSearchQuery,
  useDeleteMutation: useGroupDeleteMutation,
  useRestoreMutation: useGroupRestoreMutation,
} as const;

export interface GroupTableProps extends BasePredefinedTableProps<
  Group,
  GroupSorting,
  GroupQueryParams,
  GroupRequestBody
> {}

const BaseGroupTable = createBasePredefinedTable({
  config: GROUP_TABLE_CONFIG,
  queryHooks: GROUP_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function GroupTable(props: GroupTableProps) {
  const { t } = useTranslation("common");
  const {
    name = "group-table",
    showCreateButton = true,
    body = { deletedAt: [DeletedStatus.ACTIVE] },
    filters,
    filterValues,
    ...restProps
  } = props;

  const defaultFilters = useMemo(() => filters ?? [new DeletedStatusFilter()], [filters]);
  const defaultFilterValues = useMemo(() => filterValues ?? { deletedAt: [DeletedStatus.ACTIVE] }, [filterValues]);

  return (
    <BaseGroupTable
      searchPlaceholder={t("ram:SearchForGroup")}
      {...{
        name,
        showCreateButton,
        body,
        filters: defaultFilters,
        filterValues: defaultFilterValues,
        confirmActions: ["delete", "restore"],
        toastMessages: {
          deleteSuccess: t("ram:GroupHasBeenDeleted"),
          deleteError: t("ram:DeleteGroupError"),
          restoreSuccess: t("ram:GroupHasBeenRestored"),
          restoreError: t("ram:ErrorRestoringGroup"),
        },
        confirmLabels: {
          delete: (row: Group) => ({
            title: row.deletedAt ? t("ram:PermanentlyDeleteGroup") : t("ram:DeleteGroup"),
            text: row.deletedAt ? t("ram:PermanentlyDeleteGroupDescription") : t("ram:DeactivateGroupDescription"),
            confirmButton: row.deletedAt ? t("YesPermanentlyDelete") : t("ram:YesDelete"),
            cancelButton: t("Cancel"),
          }),
          restore: {
            title: t("ram:RestoreGroup"),
            text: t("ram:RestoreGroupText"),
            confirmButton: t("YesRestore"),
            cancelButton: t("Cancel"),
          },
        },
        ...restProps,
      }}
    />
  );
}
